import { useState } from "react";
import { Stack, Step, StepLabel, Stepper, Alert } from "@mui/material";
import { type ShiftInviteDialogProps } from "../Dialogs";
import { isDefined } from "@clipboard-health/util-ts";
import { ShiftInviteNearbyWorkers } from "../Autocomplete";
import { NearbyWorkerSearchResult, WorkerStatusLabel } from "../Autocomplete/useGetNearbyWorkers";
import { ShiftInviteReview } from "../ShiftInviteReview";
import { GroupShiftInviteDetails } from "../GroupShiftInviteDetail";
import { useShiftInviteContext } from "../Context/shiftInviteContext";
import { ShiftName } from "@src/interface";
import { utcToZonedTime } from "date-fns-tz";
import { isAfter, startOfDay, subDays } from "date-fns";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { useShiftInviteFrictionConfiguration } from "../useShiftInviteFrictionConfiguration";
import { WorkerAttendanceProfile } from "@src/containers/attendanceScore/types";
import { HighCallOffConfirmation } from "./HighCallOffConfirmation";
import {
  useWorkerAttendanceProfile,
  hasHighCallOff,
  getTotalCallOffs,
} from "../useWorkerAttendanceProfile";
import { WorkerStages, WorkerSubstages } from "@src/appV2/redesign/utils/worker";
import { InviteRequiredConfirmation } from "./InviteRequiredConfirmation";
import { BlockedWorkerMessage } from "./BlockedWorkerMessage";

export interface ShiftInviteStepperProps extends ShiftInviteDialogProps {
  onActiveStepChange?: (step: number) => void;
}

export function ShiftInviteStepper(props: ShiftInviteStepperProps) {
  const {
    workerType,
    modalState,
    initialActiveState = 0,
    initialSelectedWorker,
    onActiveStepChange,
  } = props;
  const [activeStep, setActiveStepState] = useState(initialActiveState);
  const [isProfileFetched, setIsProfileFetched] = useState<boolean>(false);
  const [selectedWorker, setSelectedWorker] = useState<NearbyWorkerSearchResult | undefined>(
    initialSelectedWorker
  );
  const [workerAttendanceProfile, setWorkerAttendanceProfile] = useState<WorkerAttendanceProfile>({
    clipboardScore: 0,
  });
  const { validateAndUpdateShiftInvite, facilityTimeZone } = useShiftInviteContext();
  const frictionConfig = useShiftInviteFrictionConfiguration();
  const zoneShiftStartTime = utcToZonedTime(props.shiftInfo.start, facilityTimeZone);
  const nowInFacilityTimeZone = utcToZonedTime(getServerNow(), facilityTimeZone);
  const selectedShiftStartsInFuture =
    isDefined(zoneShiftStartTime) && isAfter(zoneShiftStartTime, nowInFacilityTimeZone);

  const setActiveStep = (step: number) => {
    setActiveStepState(step);
    onActiveStepChange?.(step);
  };

  // Function to set selected worker
  const handleSetSelectedWorker = (worker: NearbyWorkerSearchResult | undefined) => {
    // Reset profile fetched flag when worker changes
    if (worker?.workerId !== selectedWorker?.workerId) {
      setIsProfileFetched(false);
    }
    setSelectedWorker(worker);
  };

  const currentWorkerId = selectedWorker?.workerId || initialSelectedWorker?.workerId;

  // Calculate if we need to fetch the profile
  const shouldFetchWorkerProfile = Boolean(
    currentWorkerId && !isProfileFetched && (activeStep === 0 || activeStep === 1)
  );

  const withPerformanceSince =
    frictionConfig.enabled &&
    "numberOfPastDaysToEvaluate" in frictionConfig &&
    isDefined(frictionConfig.numberOfPastDaysToEvaluate)
      ? subDays(getServerNow(), frictionConfig.numberOfPastDaysToEvaluate).toISOString()
      : undefined;

  useWorkerAttendanceProfile({
    workerId: currentWorkerId,
    enabled: shouldFetchWorkerProfile,
    onSuccess: (data) => {
      setWorkerAttendanceProfile(data);
      setIsProfileFetched(true); // Mark as fetched to prevent repeated calls
    },
    withPerformanceSince,
  });

  // Determine if the worker has high call-offs
  const maxAllowedPercentageOfCallOffs = frictionConfig.enabled
    ? frictionConfig.maxAllowedPercentageOfCallOffs
    : undefined;

  const maxAllowedCallOffs = frictionConfig.enabled ? frictionConfig.maxAllowedCallOffs : undefined;

  const hasHighCallOffRate = hasHighCallOff(
    workerAttendanceProfile,
    maxAllowedCallOffs,
    maxAllowedPercentageOfCallOffs
  );

  const isInviteRequired =
    workerAttendanceProfile.accountStatus?.status === WorkerStages.RESTRICTED &&
    workerAttendanceProfile.accountSubstatus?.name === WorkerSubstages.INVITE_REQUIRED;

  const isRestrictedWorker =
    isDefined(selectedWorker) &&
    !isInviteRequired &&
    selectedWorker.statusLabel !== WorkerStatusLabel.Blocked &&
    (selectedWorker.statusLabel === WorkerStatusLabel.Restricted ||
      workerAttendanceProfile.accountStatus?.status === WorkerStages.RESTRICTED);

  const isBlockedWorker =
    isDefined(selectedWorker) && selectedWorker.statusLabel === WorkerStatusLabel.Blocked;

  const totalCallOffs = getTotalCallOffs(workerAttendanceProfile);
  const workerFirstName = selectedWorker?.fullName?.split(" ")[0] || "Worker";

  const showHighCallOffConfirmation =
    isDefined(selectedWorker) && hasHighCallOffRate && !isBlockedWorker;
  const showInviteRequiredConfirmation =
    isDefined(selectedWorker) && isInviteRequired && !isBlockedWorker;
  const showSearchWorkersDropdown = !(
    isBlockedWorker ||
    showHighCallOffConfirmation ||
    showInviteRequiredConfirmation
  );

  // Function to clear selected worker and return to search
  const handleInviteSomeoneElse = () => {
    setSelectedWorker(undefined);
    setWorkerAttendanceProfile({ clipboardScore: 0 });
    setActiveStepState(0);
  };

  return (
    <Stack spacing={2}>
      <Stepper activeStep={activeStep}>
        <Step>
          <StepLabel
            error={activeStep === 0 && (showInviteRequiredConfirmation || isBlockedWorker)}
          >
            {isDefined(selectedWorker)
              ? `Inviting ${selectedWorker.fullName}, ${workerType}`
              : "Select a Professional"}
          </StepLabel>
        </Step>
        <Step>
          <StepLabel>Select Shift(s)</StepLabel>
        </Step>
        <Step>
          <StepLabel>Review Invites</StepLabel>
        </Step>
      </Stepper>

      {/* Display warning if worker is restricted */}
      {isRestrictedWorker && (
        <Alert
          severity="warning"
          sx={{
            mt: 1,
          }}
        >
          Reports of poor attendance or unprofessional conduct
        </Alert>
      )}

      {/* Display error if worker is blocked */}
      {isBlockedWorker && (
        <Alert
          severity="error"
          sx={{
            mt: 1,
          }}
        >
          Blocked Professional
        </Alert>
      )}

      {/* Display warning if worker has high call-off rate */}
      {frictionConfig.enabled &&
        isDefined(selectedWorker) &&
        hasHighCallOffRate &&
        !isBlockedWorker && (
          <Alert
            severity="error"
            sx={{
              mt: 1,
              "& .MuiAlert-message": {
                color: "error.main",
              },
            }}
          >
            <strong>
              {workerFirstName} has same-day cancelled {totalCallOffs} time(s) in the last 30 days.
            </strong>{" "}
            Consider inviting someone else.
          </Alert>
        )}

      {isBlockedWorker && (
        <BlockedWorkerMessage
          selectedWorker={selectedWorker}
          onInviteSomeoneElse={handleInviteSomeoneElse}
        />
      )}

      {!isBlockedWorker && (activeStep === 0 || !isDefined(selectedWorker)) && (
        <>
          {/* Show the confirmation prompt instead of search bar if worker has high call-offs */}
          {showHighCallOffConfirmation && (
            <HighCallOffConfirmation
              selectedWorker={selectedWorker}
              totalCallOffs={totalCallOffs}
              onInviteSomeoneElse={handleInviteSomeoneElse}
              onProceed={(confirmed) => {
                if (confirmed) {
                  setActiveStep(1);
                  void validateAndUpdateShiftInvite({
                    selectedShiftInfo: {
                      start: zoneShiftStartTime,
                      end: utcToZonedTime(props.shiftInfo.end, facilityTimeZone),
                      shiftType: props.shiftInfo.shiftType as ShiftName,
                      isSelected: true,
                    },
                    selectedDate: startOfDay(zoneShiftStartTime),
                    selectedWorker,
                  });
                }
              }}
            />
          )}
          {showInviteRequiredConfirmation && (
            <InviteRequiredConfirmation
              selectedWorker={selectedWorker}
              onInviteSomeoneElse={handleInviteSomeoneElse}
              onProceed={(confirmed) => {
                if (confirmed) {
                  setActiveStep(1);
                  void validateAndUpdateShiftInvite({
                    selectedShiftInfo: {
                      start: zoneShiftStartTime,
                      end: utcToZonedTime(props.shiftInfo.end, facilityTimeZone),
                      shiftType: props.shiftInfo.shiftType as ShiftName,
                      isSelected: true,
                    },
                    selectedDate: startOfDay(zoneShiftStartTime),
                    selectedWorker,
                  });
                }
              }}
            />
          )}
          {showSearchWorkersDropdown && (
            /* Show normal search interface if no high call-offs */
            <ShiftInviteNearbyWorkers
              selectedWorker={selectedWorker}
              setSelectedWorker={handleSetSelectedWorker}
              workerType={workerType}
              setActiveStep={setActiveStep}
              modalState={modalState}
              onClickNext={() => {
                if (selectedShiftStartsInFuture) {
                  void validateAndUpdateShiftInvite({
                    selectedShiftInfo: {
                      start: zoneShiftStartTime,
                      end: utcToZonedTime(props.shiftInfo.end, facilityTimeZone),
                      shiftType: props.shiftInfo.shiftType as ShiftName,
                      isSelected: true,
                    },
                    selectedDate: startOfDay(zoneShiftStartTime),
                    selectedWorker,
                  });
                }
              }}
            ></ShiftInviteNearbyWorkers>
          )}
        </>
      )}

      {!isBlockedWorker && activeStep === 1 && (
        <GroupShiftInviteDetails
          selectedWorker={selectedWorker}
          setSelectedWorker={setSelectedWorker}
          setActiveStep={setActiveStep}
          shiftInviteDialogProps={props}
        />
      )}
      {!isBlockedWorker && activeStep === 2 && (
        <ShiftInviteReview
          modalState={modalState}
          workerType={workerType}
          setActiveStep={setActiveStep}
          selectedWorker={selectedWorker}
        ></ShiftInviteReview>
      )}
    </Stack>
  );
}
